import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Webhook as WebhookIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { useToast } from '@centraoneplus/ui';
import {
  integrationService,
  type Integration,
  type Webhook,
  type WebhookDelivery,
} from '@services/integration.service';
import { useAuthStore } from '@stores/authStore';
import { canAdministerOrg } from '@/lib/roles';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { HuhokaCrmCard, isHuhokaAdmin } from './huhoka';
import {
  cohortCounts,
  connectorCard,
  inCohort,
  lastSuccessText,
  webhookHealth,
  ago,
  COHORT_TEXT,
  HEALTH_TEXT,
  type Cohort,
  type ConnectorCard,
  type Health,
} from './connectors';

// 20f — Integrations.
//
// One claim decides the whole screen: a card says when the connector last
// SUCCEEDED, not merely that it is connected. The hub writes `lastSyncAt` only
// on a successful probe, so the field already holds exactly that; it is
// labelled honestly here instead of as "last sync", which reads as "last
// attempt" and means the opposite.
//
// This grid is mounted at /integrations/connectors. /integrations itself is the
// MCP capability catalogue (tools the assistant can call, not services this
// workspace is wired to), linked from the header.

const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Header-only variant: the header's controls take the Board's 10px radius. */
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');

const COHORTS: Cohort[] = ['all', 'working', 'attention', 'off'];

// The hub's TransformInterceptor wraps every body as `{ data }` and the BFF
// passes it through, so a "list" can arrive bare or wrapped. Anything else is
// read as empty rather than crashing the page.
function asList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const inner = (body as { data?: unknown } | null)?.data;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

/** How many endpoints the page will read delivery history for. */
const WEBHOOK_SAMPLE = 8;

const HEALTH_STYLE: Record<Health, React.CSSProperties> = {
  working: {
    borderColor: 'var(--co-green-line)',
    background: 'var(--co-green-wash)',
    color: 'var(--co-green)',
  },
  failing: {
    borderColor: 'var(--co-burgundy-line)',
    background: 'var(--co-burgundy-wash)',
    color: 'var(--co-burgundy)',
  },
  attention: {
    borderColor: 'var(--co-terracotta-line)',
    background: 'var(--co-terracotta-wash)',
    color: 'var(--co-terracotta)',
  },
  idle: {
    borderColor: 'var(--co-line-strong)',
    background: 'var(--co-selected)',
    color: 'var(--co-ink-3)',
  },
  unconfigured: {
    borderColor: 'var(--co-line-strong)',
    background: 'var(--co-selected)',
    color: 'var(--co-ink-3)',
  },
};

function Badge({ health }: { health: Health }) {
  return (
    <span
      className="co-mono inline-flex items-center whitespace-nowrap rounded-[5px] border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
      style={HEALTH_STYLE[health]}
    >
      {HEALTH_TEXT[health]}
    </span>
  );
}

function ConnectorTile({
  card,
  busy,
  onTest,
}: {
  card: ConnectorCard;
  busy: boolean;
  onTest: () => void;
}) {
  return (
    <div className={clsx(CARD_CLASS, 'flex flex-col px-[18px] py-4')} style={CARD}>
      <div className="flex items-start gap-3">
        <div
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border text-[14px]"
          style={
            card.health === 'working'
              ? {
                  borderColor: 'var(--co-brass)',
                  background: 'var(--co-brass-wash)',
                  color: 'var(--co-brass-ink)',
                }
              : {
                  borderColor: 'var(--co-line)',
                  background: 'var(--co-selected)',
                  color: 'var(--co-ink-3)',
                }
          }
        >
          {integrationService.getIntegrationTypeIcon(card.type)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
            {card.name}
          </div>
          <div className="co-mono mt-1 truncate text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            {integrationService.getIntegrationTypeLabel(card.type)}
          </div>
        </div>
      </div>

      <div className="mt-[13px]">
        <Badge health={card.health} />
      </div>

      {/* The line the screen exists for. Not "connected" — when it last worked. */}
      <div
        className="mt-[10px] flex-1 text-[12px] leading-[1.5]"
        style={{
          color: card.lastSuccessAt ? 'var(--co-ink-3)' : 'var(--co-terracotta)',
        }}
      >
        {lastSuccessText(card)}
      </div>

      {card.error && (
        <p className="mt-2 line-clamp-2 text-[11.5px] leading-[1.45] text-[var(--co-burgundy)]">
          {card.error.message}
        </p>
      )}

      <div className="mt-[13px] flex items-center gap-3 border-t border-[var(--co-line-subtle)] pt-3">
        <button
          type="button"
          onClick={onTest}
          disabled={busy}
          className="co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:underline disabled:opacity-50"
        >
          {busy ? 'Testing…' : 'Test connection'}
        </button>
        <div className="flex-1" />
        {!card.enabled && (
          <span className="co-mono text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
            Disabled
          </span>
        )}
      </div>
    </div>
  );
}

export function ConnectorsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [cohort, setCohort] = useState<Cohort>('all');
  const [testing, setTesting] = useState<string | null>(null);

  const {
    data: integrations,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['integrations', 'connectors'] as const,
    queryFn: async () => asList<Integration>(await integrationService.getIntegrations()),
    staleTime: 30_000,
  });

  const { data: webhooks } = useQuery({
    queryKey: ['integrations', 'webhooks'] as const,
    queryFn: async () => asList<Webhook>(await integrationService.getWebhooks()),
    staleTime: 30_000,
  });

  // Delivery history is per endpoint, so reading all of them is one request
  // each. The page samples the first few and reports how many it skipped
  // rather than quietly describing a subset as the whole.
  const sampled = useMemo(() => (webhooks ?? []).slice(0, WEBHOOK_SAMPLE), [webhooks]);
  const deliveryQueries = useQueries({
    queries: sampled.map((hook) => ({
      queryKey: ['integrations', 'webhooks', hook.id, 'deliveries'] as const,
      queryFn: async () =>
        asList<WebhookDelivery>(await integrationService.getWebhookDeliveries(hook.id, 20)),
      staleTime: 30_000,
      retry: false,
    })),
  });

  const hooks = useMemo(() => {
    const byId = new Map(
      sampled.map((hook, i) => [hook.id, deliveryQueries[i]?.data] as const),
    );
    return webhookHealth(webhooks ?? [], byId);
  }, [webhooks, sampled, deliveryQueries]);

  const cards = useMemo(
    () => (integrations ?? []).map(connectorCard),
    [integrations],
  );
  const counts = useMemo(() => cohortCounts(cards), [cards]);
  const shown = useMemo(() => cards.filter((c) => inCohort(c, cohort)), [cards, cohort]);
  const failing = cards.filter((c) => c.health === 'failing');

  const subtitle =
    counts.all === 0
      ? 'Nothing connected yet'
      : [
          `${counts.working} connected`,
          counts.attention ? `${counts.attention} need attention` : null,
          counts.off ? `${counts.off} not connected` : null,
        ]
          .filter(Boolean)
          .join(' · ');

  const handleTest = async (card: ConnectorCard) => {
    setTesting(card.id);
    try {
      const result = await integrationService.testConnection(card.id);
      if (result.success) {
        toast.success(card.name, result.message || 'The connection worked.');
      } else {
        toast.error(card.name, result.message || 'The connection failed.');
      }
      // The probe writes status and last-success, so the grid is now stale.
      await queryClient.invalidateQueries({ queryKey: ['integrations', 'connectors'] });
    } catch (err) {
      toast.error(
        card.name,
        err instanceof Error ? err.message : 'The test could not be run.',
      );
    } finally {
      setTesting(null);
    }
  };

  // Webhook delivery logs live in Organization Settings, which only an admin
  // may open; nobody else is offered a link to a page that refuses them.
  const webhookSettings = user?.tenantId && canAdministerOrg(user)
    ? `/settings/tenant/${user.tenantId}/webhooks`
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-y-auto bg-[var(--co-canvas)] py-4 pr-4">
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle, the utility cluster in the top-right corner. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Integrations
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {subtitle}
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: cohort tabs on the left, every other header control on the right. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
            <div className="flex items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
              {COHORTS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCohort(key)}
                  aria-pressed={cohort === key}
                  className="co-mono flex items-center gap-[6px] whitespace-nowrap rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
                  style={
                    cohort === key
                      ? {
                          background: 'var(--co-raised)',
                          color: 'var(--co-ink)',
                          fontWeight: 600,
                          boxShadow: 'var(--co-shadow)',
                        }
                      : { color: 'var(--co-ink-3)', fontWeight: 500 }
                  }
                >
                  {COHORT_TEXT[key]}
                  <span
                    className="font-semibold"
                    style={{ color: cohort === key ? 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
                  >
                    {counts[key]}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              className={HEADER_CONTROL}
            >
              Capability catalogue
            </button>
            {webhookSettings && (
              <button type="button" onClick={() => navigate(webhookSettings)} className={HEADER_CONTROL}>
                <WebhookIcon className="h-[14px] w-[14px]" />
                Webhooks
              </button>
            )}
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className={HEADER_CONTROL}
            >
              <RefreshCw className={clsx('h-[14px] w-[14px]', isFetching && 'animate-spin')} />
              Refresh
            </button>
        </div>
      </header>

      {/* A failure band above the grid, not a cell inside it. A warning that
          scrolls away between two healthy cards is a warning nobody reads. */}
      {failing.map((card) => (
        <div
          key={card.id}
          className="flex flex-none items-start gap-3 rounded-[10px] border px-[18px] py-[14px]"
          style={{
            borderColor: 'var(--co-burgundy-line)',
            background: 'var(--co-burgundy-wash)',
          }}
        >
          <div className="mt-[1px] flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md bg-[var(--co-burgundy)] text-[13px] font-bold text-[var(--co-ink-invert)]">
            !
          </div>
          <div className="min-w-0 flex-1">
            <div className="co-mono text-[10px] font-semibold uppercase tracking-[.14em] text-[var(--co-burgundy)]">
              {`${card.name} is failing`}
            </div>
            <p className="mt-[5px] max-w-[92ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]">
              {card.error?.message ?? 'The hub recorded no reason.'}
              {card.error?.at && ` · recorded ${ago(card.error.at)}`}
              {' · '}
              {card.lastSuccessAt
                ? `last succeeded ${ago(card.lastSuccessAt)}`
                : 'it has never succeeded'}
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleTest(card)}
            disabled={testing === card.id}
            className="co-mono flex-none whitespace-nowrap rounded-lg border border-[var(--co-burgundy-line)] bg-[var(--co-raised)] px-3 py-[7px] text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)] disabled:opacity-50"
          >
            {testing === card.id ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      ))}

      {isLoading ? (
        <div className={clsx(CARD_CLASS, 'flex-none px-5 py-12 text-center')} style={CARD}>
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[var(--co-ink-3)]" />
        </div>
      ) : isError ? (
        <div className={clsx(CARD_CLASS, 'flex-none px-5 py-10')} style={CARD}>
          <p className="max-w-[76ch] text-[13px] leading-[1.6] text-[var(--co-ink-2)]">
            The connector list could not be read, so this page cannot say what is connected or
            whether any of it is working. That is not the same as nothing being connected —
            integrations may be delivering perfectly while this page is blind to them.
          </p>
          <button type="button" onClick={() => refetch()} className={clsx(CONTROL, 'mt-4')}>
            Try again
          </button>
        </div>
      ) : (
        <div className="grid flex-none grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-[14px] [&>*]:max-w-[640px]">
          {shown.map((card) => (
            <ConnectorTile
              key={card.id}
              card={card}
              busy={testing === card.id}
              onTest={() => handleTest(card)}
            />
          ))}

          {/* HuHoKa CRM ecosystem link (plan 14). Admins only: connecting and
              disconnecting are org-admin actions in both products. */}
          {isHuhokaAdmin(user?.role) && <HuhokaCrmCard tenantId={user?.tenantId} />}

          {/* Webhooks are the one connector this workspace always has, because
              the endpoints are the tenant's own. */}
          <div className={clsx(CARD_CLASS, 'flex flex-col px-[18px] py-4')} style={CARD}>
            <div className="flex items-start gap-3">
              <div className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border border-[var(--co-line)] bg-[var(--co-selected)] text-[var(--co-ink-3)]">
                <WebhookIcon className="h-[15px] w-[15px]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
                  Webhooks
                </div>
                <div className="co-mono mt-1 truncate text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                  Your own endpoints
                </div>
              </div>
            </div>

            <div className="mt-[13px]">
              <Badge
                health={
                  hooks.endpoints === 0
                    ? 'unconfigured'
                    : hooks.failing > 0
                      ? 'failing'
                      : hooks.enabled === 0
                        ? 'idle'
                        : 'working'
                }
              />
            </div>

            <div className="mt-[10px] flex-1 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
              {hooks.endpoints === 0 ? (
                'No endpoints registered.'
              ) : (
                <>
                  {`${hooks.endpoints} ${hooks.endpoints === 1 ? 'endpoint' : 'endpoints'} · ${hooks.enabled} enabled`}
                  {hooks.failing > 0 && (
                    <span className="text-[var(--co-burgundy)]">
                      {` · ${hooks.failing} last delivered a failure`}
                    </span>
                  )}
                  {hooks.retrying > 0 && (
                    <span className="text-[var(--co-terracotta)]">
                      {` · ${hooks.retrying} queued for retry`}
                    </span>
                  )}
                  {hooks.unchecked > 0 && (
                    <span className="text-[var(--co-ink-4)]">
                      {` · ${hooks.unchecked} not read`}
                    </span>
                  )}
                </>
              )}
            </div>

            {webhookSettings && (
              <div className="mt-[13px] flex items-center gap-3 border-t border-[var(--co-line-subtle)] pt-3">
                <button
                  type="button"
                  onClick={() => navigate(webhookSettings)}
                  className="co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:underline"
                >
                  Manage endpoints →
                </button>
              </div>
            )}
          </div>

          {shown.length === 0 && cards.length > 0 && (
            <div
              className={clsx(CARD_CLASS, 'col-span-full flex-none px-5 py-8')}
              style={CARD}
            >
              <p className="text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                {`Nothing is ${COHORT_TEXT[cohort].toLowerCase()}.`}
              </p>
            </div>
          )}
        </div>
      )}

      <div
        className={clsx(
          CARD_CLASS,
          'flex flex-none flex-wrap items-center justify-between gap-x-8 gap-y-3 px-[18px] py-[14px]',
        )}
        style={CARD}
      >
        <p
          className="min-w-0 max-w-[110ch] flex-1 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]"
          style={{ textWrap: 'pretty' } as React.CSSProperties}
        >
          Every card states{' '}
          <span className="text-[var(--co-brass-ink)]">when it last succeeded</span>, not just
          that it is connected.{' '}
          <strong className="font-semibold text-[var(--co-ink)]">
            A connected badge with no recent success is worth checking in the delivery logs.
          </strong>
        </p>
        {webhookSettings && (
          <button
            type="button"
            onClick={() => navigate(webhookSettings)}
            className="co-mono flex-none whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:underline"
          >
            View all delivery logs →
          </button>
        )}
      </div>
    </div>
  );
}

export default ConnectorsPage;
